import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { MemberSendOfferModalEndDate } from "@/modules/members/sendOfferToMember/components/memberSendOfferModalEndDate"
import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import { BoxOfferItemEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { type CouponInputFormik, DurationType } from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { MemberSendOfferModalDiscount } from "@modules/members/sendOfferToMember/components/memberSendOfferModalDiscount"
import { MemberSendOfferModalStartDate } from "@modules/members/sendOfferToMember/components/memberSendOfferModalStartDate"
import { MemberSendOfferModalStepOne } from "@modules/members/sendOfferToMember/components/memberSendOfferModalStepOne"
import { MemberSendOfferModalStepTwo } from "@modules/members/sendOfferToMember/components/memberSendOfferModalStepTwo"
import { useUserProfile } from "@modules/profile/publicProfile/hooks/useUserProfile"
import { useCallback, useImperativeHandle, useState } from "react"

export type MemberSendOfferModalRef = {
  open: () => void
} & React.ComponentProps<"div">

type MemberSendOfferModalProps = {
  isSendingToLead?: boolean
  userId: string
  memberId?: string
  ref: React.Ref<MemberSendOfferModalRef>
  className?: string
}

export function MemberSendOfferModal({
  userId,
  memberId,
  isSendingToLead = false,
  ref,
  className,
  ...props
}: MemberSendOfferModalProps) {
  const discountCouponResetValue = {
    type: CouponType.PERCENT,
    value: 0,
    durationType: DurationType.UNLIMITED,
    months: null,
  }
  const [showModal, setShowModal] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState<BoxOfferItemEntity | null>(null)
  const [step, setStep] = useState<number>(1)
  const [isDiscountVisible, setIsDiscountVisible] = useState(false)
  const [isDeferredDateVisible, setIsDeferredDateVisible] = useState(false)
  const [isEndDateVisible, setIsEndDateVisible] = useState(false)
  const [deferredDate, setDeferredDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [discountInput, setDiscountInput] = useState<CouponInputFormik>(discountCouponResetValue)
  const [paymentAtCounter, setPaymentAtCounter] = useState<boolean>(false)
  const [forceAccept, setForceAccept] = useState<boolean>(false)

  const { data: user } = useUserProfile(userId)

  const onHide = useCallback(() => {
    setShowModal(false)
    setIsDiscountVisible(false)
    setIsDeferredDateVisible(false)
    setIsEndDateVisible(false)
    setEndDate(null)
    setSelectedOffer(null)
    setDiscountInput(discountCouponResetValue)
    setDeferredDate(null)
    setIsEndDateVisible(false)
    setEndDate(null)
    setStep(1)
  }, [])

  const handleStep = () => {
    setStep((prev) => (prev === 1 ? 2 : 1))
    setDiscountInput(discountCouponResetValue)
    setDeferredDate(null)
    setEndDate(null)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  if (!user) return null

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent
        className={cn("flex h-[calc(100vh-2rem)] max-h-180 w-130 flex-col overflow-y-scroll", className)}
        {...props}
        aria-describedby={undefined}
      >
        <DialogHeader>
          {/* HEADER */}
          <div className="flex flex-col flex-wrap content-center justify-center text-center">
            <DialogTitle>
              <div className="flex items-center justify-center gap-2">
                <img src={boxOfferPlanIcon} />

                <Text fontStyle="TL" className="text-textDefault-heading">
                  {word("box.offer.send.modal.title")}
                </Text>
              </div>
            </DialogTitle>
            <Text fontStyle="BM" className="text-textDefault-caption">
              {user.firstname} {user.lastname}
            </Text>
          </div>
        </DialogHeader>

        {/* STEPS */}
        {step === 1 && showModal && (
          <MemberSendOfferModalStepOne
            selectedOffer={selectedOffer}
            setSelectedOffer={setSelectedOffer}
            handleStep={handleStep}
            onHide={onHide}
          />
        )}
        {step === 2 && selectedOffer && !isDiscountVisible && !isDeferredDateVisible && !isEndDateVisible && (
          <MemberSendOfferModalStepTwo
            boxOfferId={selectedOffer.id}
            userId={user.userId}
            memberId={memberId}
            isSendingToLead={isSendingToLead}
            handleStep={handleStep}
            onHide={onHide}
            discountInput={discountInput}
            deferredDate={deferredDate}
            endDate={endDate}
            setIsDiscountVisible={setIsDiscountVisible}
            setIsDeferredDateVisible={setIsDeferredDateVisible}
            setIsEndDateVisible={setIsEndDateVisible}
            paymentAtCounter={paymentAtCounter}
            setPaymentAtCounter={setPaymentAtCounter}
            forceAccept={forceAccept}
            setForceAccept={setForceAccept}
          />
        )}
        {step === 2 && selectedOffer && isDiscountVisible && !isDeferredDateVisible && !isEndDateVisible && (
          <MemberSendOfferModalDiscount
            boxOfferId={selectedOffer.id}
            discountInput={discountInput}
            setDiscountInput={setDiscountInput}
            setIsDiscountVisible={setIsDiscountVisible}
          />
        )}
        {step === 2 && selectedOffer && !isDiscountVisible && isDeferredDateVisible && !isEndDateVisible && (
          <MemberSendOfferModalStartDate
            deferredDate={deferredDate}
            endDate={endDate}
            setDeferredDate={setDeferredDate}
            setIsDeferredDateVisible={setIsDeferredDateVisible}
          />
        )}
        {step === 2 && selectedOffer && !isDiscountVisible && !isDeferredDateVisible && isEndDateVisible && (
          <MemberSendOfferModalEndDate
            deferredDate={deferredDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setIsEndDateVisible={setIsEndDateVisible}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
